/**
 * Envío AP-4 · «Editar imagen» fuera del timeline (Galería y nodos de imagen
 * del Canvas). Guarda una imagen NUEVA en la Galería y, si se pide, la lleva al
 * Canvas conectada a Banana 2 Pro con el pedido listo.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import useImageEditStore from '../../store/imageEditStore';
import { useEditableImage } from './useEditableImage';
import ImageEditStage from './ImageEditStage';
import ImageEditPanel from './ImageEditPanel';
import { defaultCanvasPrompt, saveEditedImage, sendToCanvas } from './imageEditActions';
import { outputSize, PREVIEW_PX } from '../../utils/imageEdit';
import '../../styles/image-edit.css';

const stop = (e) => e.stopPropagation();

function FitStage({ img }) {
  const rotate = useImageEditStore((s) => s.params.rotate);
  const ref = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const size = outputSize(img.naturalWidth || img.width, img.naturalHeight || img.height, rotate, PREVIEW_PX);
  const aspect = size.width / size.height;
  const width = Math.max(40, Math.min(box.w, box.h * aspect));
  return (
    <div className="ie-modal-stage" ref={ref}>
      {box.w > 0 && <ImageEditStage img={img} style={{ width }} />}
    </div>
  );
}

/**
 * source: { url, label, assetId }. canvasOption: muestra «Llevar al Canvas».
 * near: posición del nodo de origen (para ubicar los nodos nuevos al lado).
 */
export default function ImageEditModal({ source, onClose, onSaved, canvasOption = true, defaultToCanvas = false, near = null }) {
  const { img, status, error } = useEditableImage(source?.url);
  const open = useImageEditStore((s) => s.open);
  const close = useImageEditStore((s) => s.close);
  const params = useImageEditStore((s) => s.params);
  const saving = useImageEditStore((s) => s.saving);
  const [toCanvas, setToCanvas] = useState(defaultToCanvas);
  const [prompt, setPrompt] = useState('');
  const [promptTouched, setPromptTouched] = useState(false);

  useEffect(() => {
    open({ key: `modal:${source?.url}`, source, origin: 'modal' }, source?.params, { tool: source?.tool });
    return () => close();
    // eslint-disable-next-line
  }, [source?.url]);

  useEffect(() => { if (!promptTouched) setPrompt(defaultCanvasPrompt(params)); }, [params, promptTouched]);
  // AP-14 · la pestaña «Tamaño» estima con la imagen cargada
  useEffect(() => { useImageEditStore.getState().setImg(img || null); }, [img]);

  const apply = async () => {
    if (!img) return;
    const st = useImageEditStore.getState();
    st.setSaving(true);
    try {
      const out = await saveEditedImage({ img, params: st.params, label: source?.label });
      let nodes = null;
      if (canvasOption && toCanvas) nodes = sendToCanvas({ url: out.url, name: out.name, prompt, near });
      toast.success('Imagen nueva guardada en tu Galería', {
        description: `«${out.name.replace(/\.(png|jpg|webp)$/, '')}»${nodes ? ' · en el Canvas, conectada a Banana 2 Pro' : ''}. El original no cambió.`,
        position: 'bottom-center',
      });
      onSaved?.(out, { nodes });
      onClose?.();
    } catch (err) {
      st.setSaving(false);
      toast.error(err?.userMessage || 'No se pudo guardar la imagen editada. Probá de nuevo.');
    }
  };

  const extra = canvasOption ? (
    <div className="ie-canvas-opt">
      <label className="ie-check">
        <input type="checkbox" checked={toCanvas} onChange={() => setToCanvas((v) => !v)} data-testid="ie-to-canvas" />
        <span><b>Llevar al Canvas</b><br /><small>Crea un nodo con la imagen nueva conectado a Banana 2 Pro, con el pedido listo.</small></span>
      </label>
      {toCanvas && (
        <label className="ie-field">
          <span>Pedido para el modelo</span>
          <textarea rows={3} value={prompt} maxLength={2000}
            onChange={(e) => { setPrompt(e.target.value); setPromptTouched(true); }} data-testid="ie-canvas-prompt" />
        </label>
      )}
    </div>
  ) : null;

  return createPortal(
    <div className="ie-modal nodrag nowheel" role="dialog" aria-modal="true" aria-label="Editar imagen" data-testid="ie-modal"
      // Portal: los eventos de React igual suben al componente que lo abrió (un nodo del Canvas).
      onPointerDown={stop} onMouseDown={stop} onClick={stop} onDoubleClick={stop} onWheel={stop} onKeyDown={stop}>
      <div className="ie-modal-main">
        {status === 'ready' && img ? <FitStage img={img} /> : (
          <div className="ie-modal-state">
            {status === 'error' ? <p role="alert">{error}</p> : <><Loader2 size={20} className="ie-spin" aria-hidden="true" /> Abriendo la imagen…</>}
          </div>
        )}
      </div>
      <ImageEditPanel
        sourceLabel={source?.label}
        imgSize={img ? { w: img.naturalWidth, h: img.naturalHeight } : undefined}
        onCancel={() => { if (!saving) onClose?.(); }}
        onApply={apply}
        applying={saving}
        applyLabel="Guardar"
        footerExtra={extra}
        note={<>Se guarda como <b>imagen nueva</b> en tu Galería. <b>El original no cambia</b>.</>}
      />
    </div>,
    document.body,
  );
}
